import { useAuth } from "@clerk/clerk-react";
import { Box, IconButton, Skeleton, Switch, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { Dispatch, SetStateAction, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { fetcherAuth } from "utils/api";

import {
  settingCard,
  settingDescription,
  settingLabel,
  settingSwitch,
} from "./styles";

interface AutopilotSettingsProps {
  pauseAll: boolean;
  setPauseAll: Dispatch<SetStateAction<boolean>>;
  notifyOnNewBrands: boolean;
  setNotifyOnNewBrands: Dispatch<SetStateAction<boolean>>;
  loading: boolean;
}

export default function AutopilotSettings({
  pauseAll,
  setPauseAll,
  notifyOnNewBrands,
  setNotifyOnNewBrands,
  loading,
}: AutopilotSettingsProps) {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const navigate = useNavigate();

  const handleToggle = async () => {
    if (!currentOrg?.id) return;
    const newValue = !pauseAll;
    setPauseAll(newValue);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automation-settings`,
        "PUT",
        {},
        { pauseAll: newValue },
      );
    } catch (error) {
      setPauseAll(!newValue);
      setErrorAlert(error);
    }
  };

  const handleNotifyToggle = async () => {
    if (!currentOrg?.id) return;
    const newValue = !notifyOnNewBrands;
    setNotifyOnNewBrands(newValue);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automation-settings`,
        "PUT",
        {},
        { notifyOnNewBrands: newValue },
      );
    } catch (error) {
      setNotifyOnNewBrands(!newValue);
      setErrorAlert(error);
    }
  };

  return (
    <Box sx={{ py: 3, px: { xs: 2, sm: 3 }, maxWidth: 600 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <IconButton
          onClick={() => navigate("/dashboard/autopilot")}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            border: "1px solid rgba(1,67,57,0.15)",
            color: "rgba(1,67,57,0.6)",
            "&:hover": {
              backgroundColor: "rgba(1,67,57,0.05)",
              color: "primary.main",
            },
          }}
        >
          <Box
            component="span"
            className="fa-solid fa-arrow-left"
            sx={{ fontSize: 14 }}
          />
        </IconButton>
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 22,
              lineHeight: "28px",
              color: "primary.main",
            }}
          >
            Automation Settings
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              lineHeight: "20px",
              color: "rgba(1,67,57,0.6)",
            }}
          >
            Manage global settings for your brand automations.
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Skeleton
          variant="rounded"
          height={220}
          sx={{ mt: 3, borderRadius: "12px" }}
        />
      ) : (
        <>
          <Box sx={{ ...settingCard, mt: 3 }}>
            <Box>
              <Typography sx={settingLabel}>Pause All Automations</Typography>
              <Typography sx={settingDescription}>
                When enabled, all automations will stop discovering and pitching
                new brands. Any emails already scheduled will still be
                delivered.
              </Typography>
            </Box>
            <Switch
              checked={pauseAll}
              onChange={handleToggle}
              sx={settingSwitch}
            />
          </Box>
          <Box sx={{ ...settingCard, mt: 2 }}>
            <Box>
              <Typography sx={settingLabel}>Email Notifications</Typography>
              <Typography sx={settingDescription}>
                Receive an email each time your automations find new brands.
              </Typography>
            </Box>
            <Switch
              checked={notifyOnNewBrands}
              onChange={handleNotifyToggle}
              sx={settingSwitch}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
